import { useEffect, useState } from "react";
import { Typography, Box, Button, Card, Divider, Chip } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "../../../../../../components/breadcrumb/breadcrumb";
import Loading from "../../../../../../components/loading/loading";
import {
  getFormDetailsRequest,
  getFormDetailsSuccess,
} from "../../../../../../redux/clients/slice";
import EditForm from "./edit-form";
import ContactSpectrum from "../../../../../../components/contact-spectrum/constact-spectrum";
import { downloadTemporaryFileRequest } from "../../../../../../utils/APICalls";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import { getInspectionServicesRequest } from "../../../../../../redux/inspection-firms/slice";

export default function ClientFormSettings() {
  const navigate = useNavigate();
  const { client, form } = useLocation().state;
  const dispatch = useDispatch();

  const [showEditForm, setShowEditForm] = useState(false);
  const [localFormDetails, setLocalFormDetails] = useState<any>(null);
  const [showLoader, setShowLoader] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const { loading, formDetails } = useSelector((state: any) => state.clients);
  const { inspectionServices } = useSelector(
    (state: any) => state.inspectionFirms
  );

  useEffect(() => {
    if (inspectionServices.length === 0)
      dispatch(getInspectionServicesRequest());
    setLocalFormDetails(null);
    dispatch(getFormDetailsSuccess({ response: null }));
    dispatch(getFormDetailsRequest({ id: form?.id }));
  }, [form?.id]); // eslint-disable-line

  useEffect(() => {
    if (formDetails) {
      setLocalFormDetails(formDetails);
    }
  }, [formDetails]);

  const downloadAttachment = async (data: any) => {
    setShowLoader(true);
    try {
      const response = await downloadTemporaryFileRequest(data.attachmentKey);
      if (response && response.status === 200) {
        let fileUrl = response.data;

        if (typeof fileUrl === "string") {
          fileUrl = fileUrl.trim();
          if (fileUrl.startsWith('"') && fileUrl.endsWith('"')) {
            fileUrl = JSON.parse(fileUrl);
          }
        }

        const a = document.createElement("a");
        a.href = fileUrl;
        a.download = data?.attachmentName;
        a.target = "_blank";
        a.rel = "noopener noreferrer";
        document.body.appendChild(a);
        a.click();
        a.remove();
      } else if (response && response.status >= 400) {
        setShowErrorNotification(true);
        setErrorMessage("Failed to download document. Please try again.");
      }
    } catch (error) {
      setShowErrorNotification(true);
      setErrorMessage("Failed to download document. Please try again.");
    } finally {
      setShowLoader(false);
    }
  };

  const getInspectionTypeLabel = (value: string) => {
    const findType = inspectionServices.find(
      (t: any) => t?.inspectionType === value
    );
    return findType ? findType?.displayName : "-";
  };

  return (
    <>
      <ContactSpectrum />
      <Breadcrumb />
      <Typography sx={{ ml: 2 }} className="title">
        {localFormDetails?.displayName}
      </Typography>
      <Box
        sx={{ mt: 3 }}
        className="clients-header"
        justifyContent={"space-between"}
      >
        <Typography sx={{ fontSize: 14, color: "rgba(0, 0, 0, 0.6)" }}>
          SETTINGS
        </Typography>
        <Button
          id="view-button"
          variant="contained"
          onClick={() =>
            navigate(
              `/clients/${client?.clientName}/forms/${form?.displayName}/form-builder`,
              {
                state: localFormDetails,
              }
            )
          }
        >
          View & Edit Form
        </Button>
      </Box>

      <Card className="card-layout">
        <Box className="card-header">
          <Typography variant="h5">Settings</Typography>
          <Button
            variant="text"
            startIcon={<EditOutlinedIcon />}
            onClick={() => setShowEditForm(true)}
          >
            Edit
          </Button>
        </Box>
        <Box className="display-name-layout">
          <Typography className="label-txt">Form Name</Typography>
          <Typography>{localFormDetails?.displayName}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Category</Typography>
          <Typography>{localFormDetails?.inspectionTypeCategory}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Type</Typography>
          <Typography>
            {getInspectionTypeLabel(localFormDetails?.inspectionType)}
          </Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Available Output</Typography>
          <Box>
            {localFormDetails?.availableOutputs &&
            localFormDetails?.availableOutputs?.length > 0 ? (
              localFormDetails?.availableOutputs.map((output: string) => (
                <Typography key={output}>{output}</Typography>
              ))
            ) : (
              <>-</>
            )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Attachments</Typography>
          {localFormDetails?.attachments &&
          localFormDetails?.attachments.length > 0 ? (
            <Box>
              {localFormDetails?.attachments.map(
                (attachment: any, index: number) => (
                  <Box
                    key={attachment?.attachmentKey}
                    sx={{ mt: index === 0 ? 0 : 1 }}
                  >
                    <Typography
                      component="span"
                      color="primary"
                      sx={{
                        cursor: "pointer",
                      }}
                      onClick={() => downloadAttachment(attachment)}
                    >
                      {attachment?.attachmentName}
                    </Typography>
                  </Box>
                )
              )}
            </Box>
          ) : (
            <>-</>
          )}
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Client Fee</Typography>
          <Typography>{localFormDetails?.clientFee}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Inspector Fee</Typography>
          <Typography>{localFormDetails?.inspectorFee}</Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Photo Resolution</Typography>
          <Typography>
            {localFormDetails?.photoResolution &&
              `${localFormDetails?.photoResolution} Resolution`}
          </Typography>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Standard Restrictions</Typography>
          <Box>
            {localFormDetails?.restrictions.hasOwnProperty(
              "backgroundCheckDone"
            ) && (
              <Typography display={"flex"} alignItems={"center"}>
                Background Check Completed
                <Chip
                  sx={{ ml: 1 }}
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={
                    localFormDetails?.restrictions.backgroundCheckDone
                      ? "True"
                      : "False"
                  }
                />
              </Typography>
            )}
            {localFormDetails?.restrictions?.certifications &&
              localFormDetails?.restrictions?.certifications?.length > 0 && (
                <Typography display={"flex"} alignItems={"center"} mt={1}>
                  Certifications
                  {localFormDetails?.restrictions?.certifications.map(
                    (certification: string) => (
                      <Chip
                        key={certification}
                        sx={{ ml: 1 }}
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={certification}
                      />
                    )
                  )}
                </Typography>
              )}
            {localFormDetails?.restrictions?.insurances &&
              localFormDetails?.restrictions?.insurances?.length > 0 && (
                <Typography display={"flex"} alignItems={"center"} mt={1}>
                  Insurance Required
                  {localFormDetails?.restrictions?.insurances.map(
                    (insurance: string) => (
                      <Chip
                        key={insurance}
                        sx={{ ml: 1 }}
                        size="small"
                        color="primary"
                        variant="outlined"
                        label={insurance}
                      />
                    )
                  )}
                </Typography>
              )}
          </Box>
        </Box>
        <Divider />
        <Box className="user-name-layout">
          <Typography className="label-txt">Always Require 2nd QC</Typography>
          <Typography>
            {localFormDetails?.requireSecondQc ? "Yes" : "No"}
          </Typography>
        </Box>
      </Card>

      {showEditForm && (
        <EditForm
          showDialog={showEditForm}
          closeDialog={() => setShowEditForm(false)}
          data={localFormDetails}
        />
      )}

      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}

      {(loading || showLoader) && <Loading />}
    </>
  );
}
